import React, { useState } from 'react'
import tw, { css, styled } from 'twin.macro'

import { animated, useTransition, useSpring } from 'react-spring'
import { Dialog, DialogOverlay, DialogContent } from '@reach/dialog'
import '@reach/dialog/styles.css'
import { XCircle } from 'react-feather'
import useWindowResize from '../../hooks/useWindowResize'

const OtherBranch = styled.div`
	${tw`w-full sm:w-6/12 lg:w-4/12 mb-4 px-2`}
`
const OtherBranchImage = styled.div`
	${tw`bg-cover bg-center bg-no-repeat cursor-pointer w-full`}
	max-height: 250px;
	min-height: 250px;
	height: 100%;
	transition: all 0.2s linear;
`
const OtherBranchDetails = styled.div`
	transition: all 0.2s linear;
	${tw`w-full flex mt-4 flex-col justify-center mb-2`}

	> p {
		${tw`font-display`}

		> a {
			${tw`underline text-blue-500 hover:no-underline`}
		}
	}

	> p:not(:last-of-type) {
		${tw`mb-2`}
	}
`
const AnimatedBranchDetails = animated(OtherBranchDetails)
const DialogContents = styled(DialogContent)`
	${tw`relative`}

	&[data-reach-dialog-content] {
		${tw`w-11/12 md:w-10/12 p-4`}
	}
`
const BranchName = tw.h3`font-display text-lg font-bold`
const CloseButton = styled(XCircle)`
	${tw`cursor-pointer ml-auto absolute`}
	right: 1rem;
	top: 1rem;
`
const TableHolder = tw.div`overflow-auto mb-12`
const StaffDetailsContainer = styled.table`
	${tw`w-full table-auto mt-4`}

	> tr > th {
		${tw`font-display border px-4 py-2 w-1/5`}
	}
	> tr > td {
		min-width: 200px;
		${tw`font-display border px-4 py-2 text-center`}
		> a {
			${tw`underline text-blue-500 hover:no-underline`}
		}
	}
`
const Close = styled.button`
	${tw`font-display bg-yellow-500 hover:bg-yellow-400 p-2 rounded uppercase absolute`}
	right: 1rem;
	bottom: 1rem;
`
const ImageHolder = styled.div`
	${tw`relative cursor-pointer`}
`
const ImageOverLay = styled.a`
	content: '';
	transition: all 0.3s ease-in-out;
	background-color: rgba(55, 65, 79, 0.45);
	${tw`absolute w-full h-full left-0 right-0 top-0 bottom-0 opacity-0 hover:opacity-100 cursor-pointer flex items-center justify-center font-display text-white font-bold`}
`
const SubItemsContainer = tw.div`w-95 md:w-4/12 mb-4 mx-auto`
const SubItemsInnerContainer = styled.div`
	${tw`flex flex-col px-2 h-full`}
`
const SubItemsDetails = styled.div`
	${tw`bg-white py-4 flex flex-col h-full`}

	.officeBtn {
		${tw`mt-auto`}
		> button {
			${tw`bg-yellow-500 p-2 hover:bg-yellow-400 rounded text-white hover:text-black text-sm uppercase font-display`}
		}
	}
`

const Branch = ({ details }) => {
	const [open, setOpen] = useState(true)

	const props = useSpring({
		from: {
			height: 0,
			opacity: 0,
			transform: 'translate3d(0,-15px,0)',
			overflow: 'hidden',
		},
		to: {
			height: open ? 'auto' : 0,
			opacity: open ? 1 : 0,
			transform: `translate3d(0,0,0)`,
		},
		reset: false,
	})

	const AnimatedDialogOverlay = animated(DialogOverlay)
	const AnimatedDialogContent = animated(DialogContents)
	const [showDialog, setShowDialog] = useState(false)
	const transitions = useTransition(showDialog, null, {
		from: { opacity: 0, y: -10 },
		enter: { opacity: 1, y: 0 },
		leave: { opacity: 0, y: 10 },
	})

	const { width } = useWindowResize()
	const linkAddress = details.node.branchAddress
		.split(' ')
		.join('+')
		.split('/')
		.join('%2F')
	return (
		<SubItemsContainer>
			<SubItemsInnerContainer>
				<SubItemsDetails>
					<ImageHolder>
						<a
							href={`https://www.google.com/maps/place/${linkAddress}/`}
							target="_blank"
						>
							<OtherBranchImage
								style={{
									backgroundImage: `url(${
										details.node.branchImage != null
											? details.node.branchImage.fluid.src
											: details.node.staticMapUrl.staticMapUrl
									})`,
								}}
							/>
						</a>
						{width > 767 ? (
							<ImageOverLay
								href={`https://www.google.com/maps/place/${linkAddress}/`}
								target="_blank"
							>
								<p>{details.node.branchName}</p>
							</ImageOverLay>
						) : (
							''
						)}
					</ImageHolder>
					<AnimatedBranchDetails
						className={open ? 'active' : 'notactive'}
						style={props}
					>
						{details.node.branchName != null ? (
							<p>
								<strong>Branch: </strong>
								{details.node.branchName}
							</p>
						) : (
							''
						)}
						{details.node.branchAddress != null ? (
							<p>
								<strong>Address: </strong>
								{details.node.branchAddress}
							</p>
						) : (
							''
						)}
						{details.node.phoneNumber != null ? (
							<p>
								<strong>Phone Number: </strong>
								<a
									className="phone"
									href={`tel:${details.node.phoneNumber.split(' ').join('')}`}
								>
									{details.node.phoneNumber}
								</a>
							</p>
						) : (
							''
						)}
						{details.node.email != null ? (
							<p>
								<strong>Email: </strong>
								<a className="email" href={`mailto:${details.node.email}`}>
									{details.node.email}
								</a>
							</p>
						) : (
							''
						)}

						{transitions.map(
							({ item, key, props: styles }) =>
								item && (
									<AnimatedDialogOverlay
										onClick={() => setShowDialog(false)}
										css={{ opacity: styles.opacity }}
									>
										<AnimatedDialogContent
											css={{
												transform: styles.y.interpolate(
													value => `translate3d(0px, ${value}px, 0px)`
												),
												border: '1px solid hsla(0, 0%, 0%, 0.5)',
												borderRadius: 10,
											}}
										>
											<BranchName>{details.node.branchName} Staff</BranchName>
											<CloseButton onClick={() => setShowDialog(false)} />
											<TableHolder>
												<StaffDetailsContainer>
													<tr>
														<th>Name</th>
														<th>Position</th>
														<th>Mobile</th>
														<th>LandLine</th>
														<th>Email</th>
													</tr>
													{details.node.branchStaff != null
														? details.node.branchStaff.map((x, i) => {
																return (
																	<tr>
																		<td>{x.name ? x.name : ''}</td>
																		<td>{x.position ? x.position : ''}</td>
																		<td>
																			{x.mobileNumber ? (
																				<a
																					className="phone"
																					href={`tel:${x.mobileNumber
																						.split(' ')
																						.join('')}`}
																				>
																					{x.mobileNumber}
																				</a>
																			) : (
																				''
																			)}
																		</td>
																		<td>
																			{x.landlineNumber ? (
																				<a
																					className="phone"
																					href={`tel:${x.landlineNumber
																						.split(' ')
																						.join('')}`}
																				>
																					{x.landlineNumber}
																				</a>
																			) : (
																				''
																			)}
																		</td>
																		<td>
																			{x.email ? (
																				<a
																					className="email"
																					href={`mailto:${x.email}`}
																				>
																					{x.email}
																				</a>
																			) : (
																				''
																			)}
																		</td>
																	</tr>
																)
														  })
														: ''}
												</StaffDetailsContainer>
											</TableHolder>
											<Close onClick={() => setShowDialog(false)}>Close</Close>
										</AnimatedDialogContent>
									</AnimatedDialogOverlay>
								)
						)}
					</AnimatedBranchDetails>
					<p className="officeBtn">
						<button
							onClick={() => setShowDialog(true)}
						>{`Show ${details.node.branchName} Contacts`}</button>
					</p>
				</SubItemsDetails>
			</SubItemsInnerContainer>
		</SubItemsContainer>
	)
}

export default Branch
